import { useMemo, useState, type ChangeEvent } from "react";
import {
  ArrowLeftRight,
  Loader2,
  MapPin,
  ShieldCheck,
  UserRoundSearch,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/library/dialog";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/library/avatar";
import SearchInput from "@/components/ui/searchInput";
import { getInitials } from "@/utils/convetors";
import { cn } from "@/lib/utils";
import useSwitchUserCandidates, {
  type SwitchUserCandidate,
} from "./useSwitchUserCandidates";
import { switchToUser } from "./impersonationActions";

interface SwitchUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Xale's canvas tone, one notch lighter than the app background. */
const CANVAS = "color-mix(in srgb, var(--color-background) 55%, #ffffff)";

type RoleGroup = {
  key: string;
  name: string;
  hierarchy: number;
  users: SwitchUserCandidate[];
};

function groupByRole(users: SwitchUserCandidate[]): RoleGroup[] {
  const groups = new Map<string, RoleGroup>();
  for (const u of users) {
    const key = u.role ? String(u.role.id) : "none";
    const existing = groups.get(key);
    if (existing) {
      existing.users.push(u);
      continue;
    }
    groups.set(key, {
      key,
      name: u.role?.name ?? "No role",
      hierarchy: u.role?.hierarchy ?? Number.MAX_SAFE_INTEGER,
      users: [u],
    });
  }
  // Senior roles first — the server already sorts, this keeps groups stable
  // after a client-side search narrows the list.
  return [...groups.values()].sort((a, b) => a.hierarchy - b.hierarchy);
}

/**
 * "Kochi, Kottayam +6" — a multi-branch manager can hold every branch in the
 * org; the full list still rides in the title attribute and the search.
 */
function formatBranches(branches: { name: string }[]): {
  shown: string[];
  extra: number;
  full: string;
} {
  const names = branches.map((b) => b.name).filter(Boolean);
  return {
    shown: names.slice(0, 2),
    extra: Math.max(0, names.length - 2),
    full: names.join(", "),
  };
}

function matches(u: SwitchUserCandidate, q: string): boolean {
  if (!q) return true;
  const hay = [
    u.name,
    u.email,
    u.role?.name ?? "",
    ...u.branches.map((b) => b.name),
  ]
    .join(" ")
    .toLowerCase();
  return q
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => hay.includes(term));
}

const people = (n: number) => `${n} ${n === 1 ? "person" : "people"}`;

/* ───────────────────────────── building blocks ───────────────────────────── */

/** A quiet in-dialog message — no dashed boxes, same canvas as the list. */
function ListMessage({
  icon: Icon,
  title,
  description,
  tone = "muted",
}: {
  icon: typeof Users;
  title: string;
  description: string;
  tone?: "muted" | "error";
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-8 py-14 text-center">
      <span
        className={cn(
          "flex size-12 items-center justify-center rounded-[16px]",
          tone === "error"
            ? "bg-[color-mix(in_srgb,var(--color-high)_12%,#ffffff)] text-[var(--color-high)]"
            : "bg-[var(--color-white)] text-[var(--color-black-5)] ring-1 ring-[var(--color-black-1)]",
        )}
      >
        <Icon size={20} strokeWidth={1.6} />
      </span>
      <div className="max-w-[320px]">
        <p className="text-b3-mid text-[var(--color-black-9)]">{title}</p>
        <p className="mt-1 text-b4 leading-[1.5] text-[var(--color-black-6)]">
          {description}
        </p>
      </div>
    </div>
  );
}

/** Loading placeholders shaped like the real rows — no spinner jump. */
function RowSkeletons() {
  return (
    <div className="space-y-2 px-4 pb-4 pt-4">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-[16px] bg-[var(--color-white)] p-3"
        >
          <span className="size-10 shrink-0 animate-pulse rounded-full bg-[var(--color-black-1)]" />
          <div className="flex-1 space-y-2">
            <span
              className="block h-3 animate-pulse rounded-full bg-[var(--color-black-1)]"
              style={{ width: `${45 - i * 5}%` }}
            />
            <span
              className="block h-2.5 animate-pulse rounded-full bg-[var(--color-black-1)]"
              style={{ width: `${65 - i * 6}%` }}
            />
          </div>
          <span className="h-8 w-[84px] shrink-0 animate-pulse rounded-[10px] bg-[var(--color-black-1)]" />
        </div>
      ))}
    </div>
  );
}

/* ──────────────────────────────── the dialog ─────────────────────────────── */

/**
 * "Switch user": pick someone under you and see Xale exactly as they do.
 * The list is the server's verdict (roles under yours, exclusions honoured,
 * your branches); the search only narrows it. Choosing a row switches at
 * once — switching back is one click on the banner — and the whole app
 * reloads on the new session (see impersonationActions).
 */
export default function SwitchUserDialog({
  open,
  onOpenChange,
}: SwitchUserDialogProps) {
  const { data, isLoading, isError } = useSwitchUserCandidates(open);
  const [query, setQuery] = useState("");
  const [switchingId, setSwitchingId] = useState<number | null>(null);

  const users = data ?? [];
  const q = query.trim().toLowerCase();
  const groups = useMemo(
    () => groupByRole(users.filter((u) => matches(u, q))),
    [users, q],
  );
  const shown = groups.reduce((n, g) => n + g.users.length, 0);
  const busyAnywhere = switchingId != null;
  /** Nothing to narrow — an empty list or a failed one gets no search box. */
  const showSearch = isLoading || (!isError && users.length > 0);

  const handlePick = async (u: SwitchUserCandidate) => {
    if (busyAnywhere) return;
    setSwitchingId(u.id);
    toast.loading(`Switching to ${u.name}…`, { id: "switch-user" });
    try {
      await switchToUser(u);
      // The page is reloading; keep the toast until it goes.
    } catch (err) {
      const axiosMessage = (
        err as { response?: { data?: { message?: string } } } | null
      )?.response?.data?.message;
      toast.error(
        axiosMessage ||
          (err instanceof Error ? err.message : "") ||
          `Couldn't switch to ${u.name}.`,
        { id: "switch-user" },
      );
      setSwitchingId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busyAnywhere) return;
        if (!next) setQuery("");
        onOpenChange(next);
      }}
    >
      {/* A flex COLUMN, not the library's grid: the list is the only part
          that scrolls (`flex-1 min-h-0`), so the header and footer stay put
          however long the team is. Every child still needs `min-w-0` — one
          unbreakable email would otherwise widen the whole dialog. */}
      <DialogContent
        showCloseButton={false}
        className={cn(
          "flex max-h-[86vh] flex-col gap-0 overflow-hidden border-0 p-0",
          "rounded-[28px] bg-[var(--color-white)] shadow-[0_28px_80px_rgba(5,25,18,0.20)]",
          "sm:max-w-[620px]",
        )}
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="shrink-0 px-6 pb-5 pt-6">
          <div className="flex items-start gap-3.5 pr-10">
            <span
              className="flex size-11 shrink-0 items-center justify-center rounded-[14px] text-[var(--color-success)]"
              style={{ background: "var(--xale-hero-gradient-soft)" }}
            >
              <ArrowLeftRight size={19} />
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              {/* Colour via style, not a `text-[…]` class: the library's
                  DialogTitle / DialogDescription merge their own classes
                  through tailwind-merge, which reads Xale's `text-b*` size
                  utilities as COLOURS and drops them next to another colour. */}
              <DialogTitle
                className="text-b2-mid tracking-[-0.32px]"
                style={{ color: "var(--color-black-9)" }}
              >
                Switch user
              </DialogTitle>
              <DialogDescription
                className="mt-1 text-b4 leading-[1.5]"
                style={{ color: "var(--color-black-6)" }}
              >
                See Xale exactly as one of your team does — their leads,
                follow-ups, dashboard and permissions. You can switch back at
                any time.
              </DialogDescription>
            </div>
          </div>

          {showSearch && (
            <div className="mt-5 min-w-0">
              <SearchInput
                isKbd={false}
                placeholder="Search by name, email, role or branch…"
                bgColor="white"
                value={query}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setQuery(e.target.value)
                }
                className="w-full max-w-none"
              />
            </div>
          )}
        </div>

        {/* Our own close control: the library's is a bare 16px glyph, too
            small next to a 28px-radius shell. */}
        <button
          type="button"
          aria-label="Close"
          disabled={busyAnywhere}
          onClick={() => onOpenChange(false)}
          className="absolute right-5 top-5 flex size-9 items-center justify-center rounded-full text-[var(--color-black-6)] transition-colors hover:bg-[var(--color-black-1)] hover:text-[var(--color-black-9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-green-5)] disabled:opacity-40"
        >
          <X size={18} />
        </button>

        {/* ── List, on Xale's canvas ─────────────────────────────────────── */}
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-[var(--color-black-1)]"
          style={{ background: CANVAS }}
        >
          {isLoading ? (
            <RowSkeletons />
          ) : isError ? (
            <ListMessage
              tone="error"
              icon={Users}
              title="Couldn't load your team"
              description="We couldn't fetch the people you can switch to. Close this and try again in a moment."
            />
          ) : users.length === 0 ? (
            <ListMessage
              icon={Users}
              title="No one to switch to"
              description="Nobody in a role under yours shares one of your branches yet."
            />
          ) : shown === 0 ? (
            <ListMessage
              icon={UserRoundSearch}
              title="No matches"
              description={`Nobody here matches “${query.trim()}”. Try a name, an email, a role or a branch.`}
            />
          ) : (
            <div className="px-4 pb-4 pt-3">
              {groups.map((g) => (
                <div key={g.key} className="mb-5 last:mb-0">
                  <div
                    className="sticky top-0 z-10 -mx-4 mb-2 px-5 pb-2 pt-1"
                    style={{ background: CANVAS }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="shrink-0 text-b5-mid uppercase tracking-[0.1em] text-[var(--color-black-5)]">
                        {g.name}
                      </span>
                      <span className="h-px flex-1 bg-[var(--color-black-2)]" />
                      <span className="shrink-0 rounded-full bg-[var(--color-white)] px-2 py-[2px] text-b5-mid text-[var(--color-black-6)]">
                        {g.users.length}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {g.users.map((u) => {
                      const busy = switchingId === u.id;
                      const branches = formatBranches(u.branches);
                      return (
                        <button
                          key={u.id}
                          type="button"
                          disabled={busyAnywhere}
                          onClick={() => void handlePick(u)}
                          className={cn(
                            "group flex w-full items-center gap-3.5 rounded-[16px] border border-transparent bg-[var(--color-white)] p-3 text-left",
                            "transition-[border-color,box-shadow,transform] duration-150",
                            "hover:border-[var(--color-green-5)] hover:shadow-[0_8px_24px_rgba(5,25,18,0.07)]",
                            "focus-visible:border-[var(--color-green-7)] focus-visible:outline-none focus-visible:shadow-[0_8px_24px_rgba(5,25,18,0.07)]",
                            busyAnywhere && !busy && "opacity-45",
                            busy &&
                              "border-[var(--color-green-7)] shadow-[0_8px_24px_rgba(5,25,18,0.07)]",
                          )}
                        >
                          <Avatar className="size-10 shrink-0 ring-1 ring-[var(--color-black-1)]">
                            <AvatarImage src={u.avatar ?? undefined} alt="" />
                            <AvatarFallback className="bg-[var(--color-green-2)] text-b4-mid text-[var(--color-black-9)]">
                              {getInitials(u.name)}
                            </AvatarFallback>
                          </Avatar>

                          <div className="min-w-0 flex-1">
                            <div className="flex min-w-0 items-center gap-2">
                              <span className="min-w-0 truncate text-b3-mid tracking-[-0.2px] text-[var(--color-black-9)]">
                                {u.name}
                              </span>
                              {!u.isActive && (
                                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[var(--color-black-1)] px-2 py-[2px] text-b5 text-[var(--color-black-6)]">
                                  <span className="size-1.5 rounded-full bg-[var(--color-black-4)]" />
                                  Inactive
                                </span>
                              )}
                            </div>
                            <p
                              className="mt-0.5 truncate text-b4 text-[var(--color-black-6)]"
                              title={u.email}
                            >
                              {u.email}
                            </p>
                            {branches.shown.length > 0 && (
                              <div
                                className="mt-2 flex min-w-0 items-center gap-1.5"
                                title={branches.full}
                              >
                                <MapPin
                                  size={12}
                                  className="shrink-0 text-[var(--color-black-4)]"
                                />
                                {branches.shown.map((name) => (
                                  <span
                                    key={name}
                                    className="max-w-[9rem] truncate rounded-md bg-[var(--color-green-1)] px-1.5 py-[2px] text-b5 text-[var(--color-success)]"
                                  >
                                    {name}
                                  </span>
                                ))}
                                {branches.extra > 0 && (
                                  <span className="shrink-0 rounded-md bg-[var(--color-black-1)] px-1.5 py-[2px] text-b5 text-[var(--color-black-6)]">
                                    +{branches.extra}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Fixed-width slot: the pill only changes colour on
                              hover, so nothing in the row ever moves. */}
                          <span
                            className={cn(
                              "flex w-[92px] shrink-0 items-center justify-center gap-1.5 rounded-[10px] border px-2.5 py-1.5 text-b4-mid transition-colors",
                              busy
                                ? "border-[var(--color-green-7)] bg-[var(--color-green-1)] text-[var(--color-success)]"
                                : "border-[var(--color-black-1)] bg-[var(--color-white)] text-[var(--color-black-5)] group-hover:border-[var(--color-green-5)] group-hover:bg-[var(--color-green-1)] group-hover:text-[var(--color-success)] group-focus-visible:border-[var(--color-green-5)] group-focus-visible:bg-[var(--color-green-1)] group-focus-visible:text-[var(--color-success)]",
                            )}
                          >
                            {busy ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <ArrowLeftRight size={14} />
                            )}
                            {busy ? "Switching" : "Switch"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div className="flex shrink-0 items-center justify-between gap-4 border-t border-[var(--color-black-1)] bg-[var(--color-white)] px-6 py-3.5">
          <p className="flex min-w-0 items-center gap-2 text-b5 text-[var(--color-black-6)]">
            <ShieldCheck
              size={14}
              className="shrink-0 text-[var(--color-black-4)]"
            />
            <span className="truncate">
              Only people in roles under yours, in your branches.
            </span>
          </p>
          {!isLoading && !isError && users.length > 0 && (
            <span className="shrink-0 text-b5 text-[var(--color-black-5)]">
              {q ? `${shown} of ${people(users.length)}` : people(users.length)}
            </span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
